import ExcelJS from "exceljs";

const normalizeNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;
  try {
    let s = String(v).trim();
    // do not treat period-like strings (e.g. '01/2024') as numbers
    if (s.includes('/')) return null;
    // remove currency symbols and non numeric except . and , and -
    s = s.replace(/[R$\s\u00A0]/g, "");
    // handle thousands/decimal separators in both BR and EN styles
    if (/^-?[\d.,]+$/.test(s)) {
      const hasDot = s.includes(".");
      const hasComma = s.includes(",");
      let normalized = s;
      if (hasDot && hasComma) {
        // BR style: 1.234,56
        normalized = s.replace(/\./g, "").replace(/,/g, ".");
      } else if (hasComma) {
        // Decimal comma: 1234,56
        normalized = s.replace(/,/g, ".");
      } else {
        // Decimal point: 1234.56
        normalized = s;
      }
      const n = parseFloat(normalized);
      return Number.isNaN(n) ? null : n;
    }
    // fallback parseFloat
    // if contains unexpected characters (like '/') do not parse
    if (/[^0-9.,\-]/.test(s)) return null;
    const n = parseFloat(s.replace(/\./g, "").replace(/,/g, "."));
    return isNaN(n) ? null : n;
  } catch {
    return null;
  }
};

export const exportExcel = async (retrieveData) => {
  try {
    const [exportData, exportFilename] = retrieveData;
    if (!exportData || exportData.length === 0) throw new Error("Nenhum dado para exportar");

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Resumo Geral");

    const headers = Object.keys(exportData[0] || {});

    // Title row (merged)
    const title = exportFilename || "Relatório";
    worksheet.mergeCells(1, 1, 1, headers.length);
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = "Resumo da Folha";
    titleCell.font = { name: "SansSerif", size: 24, bold: true, color: { argb: "000080" } };
    titleCell.alignment = { vertical: "middle", horizontal: "left" };

    // Extract some info for subtitle: CNPJ and period range
    const allPeriods = exportData
      .map((r) => r["Período"] || r["periodoApuracao"] || r["Referência"] || "")
      .filter(Boolean);
    const uniquePeriods = Array.from(new Set(allPeriods));
    const periodKey = (periodValue) => {
      const s = String(periodValue || "").trim();
      if (/^\d{2}\/\d{4}$/.test(s)) {
        const [mm, yyyy] = s.split("/");
        return Number(`${yyyy}${mm}`);
      }
      if (/^\d{4}-\d{2}$/.test(s)) return Number(s.replace("-", ""));
      if (/^\d{6}$/.test(s)) return Number(s);
      return Number.MAX_SAFE_INTEGER;
    };
    const sortedPeriods = uniquePeriods.sort((a, b) => periodKey(a) - periodKey(b));
    const periodRange = sortedPeriods.length === 0 ? "" : (sortedPeriods[0] === sortedPeriods[sortedPeriods.length - 1] ? sortedPeriods[0] : `${sortedPeriods[0]} - ${sortedPeriods[sortedPeriods.length - 1]}`);

    const empresa =
      exportData.find((r) => (r["CNPJ"] || r["cnpj"]))?.["CNPJ"] ||
      exportData.find((r) => (r["CNPJ"] || r["cnpj"]))?.["cnpj"] ||
      String(sessionStorage.getItem("cnpjSelecionado") || "");

    worksheet.mergeCells(2, 1, 2, headers.length);
    const infoCell = worksheet.getCell(2, 1);
    infoCell.value = `Empresa: ${empresa}    Período: ${periodRange}`.trim();
    infoCell.font = { name: "SansSerif", size: 14 };
    infoCell.alignment = { vertical: "middle", horizontal: "left" };

    // leave an empty row
    const headerRowIndex = 4;

    // header styles
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(headerRowIndex, index + 1);
      cell.value = header;
      cell.font = { bold: true, size: 12, color: {argb: "#18316e"} };
      // force header alignment to left
      cell.alignment = { horizontal: "middle", vertical: "middle" };
      cell.fill = { type: 'pattern', pattern:'solid', fgColor:{argb:'F5F5F5'} };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // list of columns that should be formatted as currency
    const currencyCols = new Set([
      'Valor Base','INSS','Terceiros','Rat','Total','INSS Corrigido','Terceiros Corrigido','Rat Corrigido','Total Corrigido','Valor Correção','Total Geral','totalGeral','totalGeralCorrigido'
    ]);

    // preferred widths to avoid huge columns for small fields
    const preferredWidths = {
      'Período': 12,
      'CNPJ': 18,
      'Código': 12,
      'Evento': 40,
      'Incidência': 60,
      'Valor Base': 14,
      'INSS': 14,
      'Terceiros': 14,
      'Rat': 14,
      'Total': 14,
      'INSS Corrigido': 14,
      'Terceiros Corrigido': 14,
      'Rat Corrigido': 14,
      'Total Corrigido': 16,
      'Selic': 8,
      'Valor Correção': 16,
    };

    // write data rows starting at headerRowIndex + 1
    exportData.forEach((dataRow, rowIndex) => {
      const rowNumber = headerRowIndex + 1 + rowIndex;
      const row = worksheet.getRow(rowNumber);

      headers.forEach((header, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        const rawValue = dataRow[header];

        const num = normalizeNumber(rawValue);
        if (num !== null) {
          // write numeric value and format as number (no currency symbol)
          cell.value = num;
          if (currencyCols.has(header)) {
            // Excel number format without currency symbol
            cell.numFmt = '#,##0.00';
          }
          // enforce right alignment for numeric cells
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        } else {
          // sanitize string values by removing any R$ occurrences and NBSPs
          let textVal = rawValue !== undefined && rawValue !== null ? String(rawValue) : "";
          try { textVal = textVal.replace(/R\$\s?/g, '').replace(/\u00A0/g, ''); } catch (e) {}
          cell.value = textVal;
          // enforce left alignment for non-numeric cells
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
      });

      // Bold subtotal / total rows if 'Incidência' contains 'Sub Total' or 'Total Relatório'
      const incidenciaVal = (dataRow['Incidência'] || '') + '';
      if (/Sub Total/i.test(incidenciaVal) || /Total Relatório/i.test(incidenciaVal) || /Sub Total Mês/i.test(incidenciaVal)) {
        row.font = { bold: true };
      }
    });

    // set column widths (preferred or auto, but with sensible caps)
    worksheet.columns.forEach((column, idx) => {
      const header = headers[idx] || '';
      if (preferredWidths[header]) {
        column.width = preferredWidths[header];
        return;
      }
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value === null || cell.value === undefined ? '' : String(cell.value);
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = Math.min(Math.max(maxLength + 2, 10), 40);
    });

    // Segunda aba: Resumo das Categorias (relatório cinza/azul idêntico ao final da prévia)
    const abaResumoCategorias = retrieveData[2];
    if (abaResumoCategorias && abaResumoCategorias.rows && abaResumoCategorias.rows.length >= 0) {
      const ws2 = workbook.addWorksheet(abaResumoCategorias.sheetName || "Resumo das Categorias", { views: [{ rightToLeft: false }] });
      const headersResumo = ["Item", "Verba Trabalhista (Rubricas)", "Fundamentação", "Valor"];
      headersResumo.forEach((h, idx) => {
        const cell = ws2.getCell(1, idx + 1);
        cell.value = h;
        cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF002d72" } };
        cell.alignment = { horizontal: "left", vertical: "middle" };
        cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
      });
      abaResumoCategorias.rows.forEach((row, rowIdx) => {
        const rowNum = rowIdx + 2;
        const cells = [
          row.item ?? "",
          row.verba ?? "",
          row.fundamentacao ?? "",
          typeof row.valor === "number" ? row.valor : (normalizeNumber(row.valor) ?? row.valor ?? ""),
        ];
        const fillArg = row.cor ? (row.cor.startsWith("#") ? "FF" + row.cor.slice(1) : "FF" + row.cor) : null;
        cells.forEach((val, colIdx) => {
          const cell = ws2.getCell(rowNum, colIdx + 1);
          cell.value = val;
          if (fillArg) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillArg } };
          cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
          if (colIdx === 3 && typeof val === "number") {
            cell.numFmt = "#,##0.00";
            cell.alignment = { horizontal: "right", vertical: "middle" };
          } else {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          }
          if (row.bold) cell.font = { bold: true };
        });
      });
      ws2.getColumn(1).width = 8;
      ws2.getColumn(2).width = 40;
      ws2.getColumn(3).width = 80;
      ws2.getColumn(4).width = 16;
    }

    await workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${exportFilename}.xlsx`;
      link.click();
    });
  } catch (error) {
    console.error(error);
    throw new Error("Erro ao exportar arquivo Excel");
  }
};
